import { useToast } from '@mdvip/shared/hooks/use-toast';
import { type RowData, useTable } from '@tanstack/react-table';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALPHA_BUCKETS, type AlphaBucket, alphaBucket } from '@/lib/member-views/alphaKey';
import { buildCsv } from '@/lib/member-views/csv';
import { downloadFile } from '@/lib/member-views/downloadFile';
import {
  type MemberColumnDef,
  type MemberDataset,
  type MemberOnSortingChange,
  type MemberSortingState,
  type MemberTable,
  memberTableFeatures,
} from '@/lib/member-views/memberDataset.types';
import { useDatasetColumns } from './useDatasetColumns';

export const PAGE_SIZES = [10, 20, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

export type DatasetState = 'loading' | 'error' | 'empty' | 'loaded';
export type AlphaSelection = 'all' | AlphaBucket;

export interface MemberDatasetView<TRow extends RowData> {
  state: DatasetState;
  retry: () => void;
  alpha: {
    letters: Array<{ letter: AlphaBucket; enabled: boolean }>;
    selected: AlphaSelection;
    onSelect: (letter: AlphaSelection) => void;
  };
  sort: { state: MemberSortingState; onChange: MemberOnSortingChange };
  pagination: {
    pageSize: PageSize;
    /** 1-based. */
    page: number;
    pageCount: number;
    setPageSize: (size: PageSize) => void;
    setPage: (page: number) => void;
  };
  /** The rows on the current page. */
  pageRows: TRow[];
  /** Every row matching the selected letter, in the current sort, across all pages — what an export takes. */
  filteredRows: TRow[];
  /** The search box's value. Nothing matches on it yet. */
  search: { value: string; onChange: (value: string) => void };
  /** The table instance the table surface renders. */
  table: MemberTable<TRow>;
  /** Downloads `filteredRows` as the dataset's CSV report. A failure raises a toast with Try again. */
  export: {
    run: () => void;
    /**
     * Always false today: the report is built and downloaded synchronously inside the click, since
     * a browser may silently drop a download that starts after the click has returned.
     */
    pending: boolean;
    /** The last run's failure, cleared by a run that succeeds. */
    error: unknown | null;
    retry: () => void;
  };
}

export interface MemberDatasetOptions<TRow extends RowData> {
  /** The table's columns, for a view that changes the dataset's defaults. Omitted means every table field, as-is. */
  columns?: MemberColumnDef<TRow>[];
}

/** One stable empty list while rows are loading, so the table doesn't see new data every render. */
const NO_ROWS: never[] = [];

/**
 * Everything a view does with its rows, without rendering any of it: loads the dataset's
 * full row set, then letters, sorts and pages it in the browser.
 *
 * The page goes back to 1 whenever what's being paged changes — the page size, the sort, the letter
 * or the view itself.
 */
export function useMemberDataset<TRow extends RowData>(
  dataset: MemberDataset<TRow>,
  options: MemberDatasetOptions<TRow> = {},
): MemberDatasetView<TRow> {
  const { t } = useTranslation('providers');
  const { toast, dismiss } = useToast();
  const query = dataset.useRows();
  const defaultColumns = useDatasetColumns(dataset).all;
  const columns = options.columns ?? defaultColumns;

  const [viewKey, setViewKey] = useState(dataset.key);
  const [letter, setLetter] = useState<AlphaSelection>('all');
  const [sorting, setSorting] = useState<MemberSortingState>(dataset.defaultSort ?? []);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSizeState] = useState<PageSize>(PAGE_SIZES[0]);
  const [search, setSearch] = useState('');
  const [exportError, setExportError] = useState<unknown>(null);
  // The failure toast stays until dismissed, so the next run clears it: a success leaves no stale
  // error behind, and repeated failures show one toast, not a stack.
  const exportToast = useRef<string | number | null>(null);

  // A different view reusing this hook starts over: React's adjust-state-during-render pattern,
  // so the stale view's page and letter never reach the screen.
  if (dataset.key !== viewKey) {
    setViewKey(dataset.key);
    setLetter('all');
    setSorting(dataset.defaultSort ?? []);
    setPageIndex(0);
  }

  const rows = query.data ?? NO_ROWS;
  const bucketOf = (row: TRow): AlphaBucket => alphaBucket(dataset.alphaKey?.(row) ?? null);

  // Built from every loaded row, so a letter stays available while another one is selected.
  const present = new Set(dataset.alphaKey ? rows.map(bucketOf) : []);
  const letters = ALPHA_BUCKETS.map((bucket) => ({ letter: bucket, enabled: present.has(bucket) }));
  const data = letter === 'all' ? rows : rows.filter((row) => bucketOf(row) === letter);

  // The rows can shrink out from under a later page — a background refetch that drops patients —
  // so land on the last page that still has rows rather than an empty "page 3 of 2".
  const lastPageIndex = Math.max(Math.ceil(data.length / pageSize) - 1, 0);
  if (pageIndex > lastPageIndex) setPageIndex(lastPageIndex);
  const currentPageIndex = Math.min(pageIndex, lastPageIndex);

  const onSortingChange: MemberOnSortingChange = (updater) => {
    setSorting(updater);
    setPageIndex(0);
  };

  const setPageSize = (size: PageSize) => {
    setPageSizeState(size);
    setPageIndex(0);
  };

  const table = useTable({
    features: memberTableFeatures,
    columns,
    data,
    getRowId: (row) => dataset.rowKey(row),
    state: { sorting, pagination: { pageIndex: currentPageIndex, pageSize } },
    onSortingChange,
    // The table surface may page through the table rather than the hook; both land in one place.
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: currentPageIndex, pageSize }) : updater;
      if (next.pageSize !== pageSize) setPageSize(next.pageSize as PageSize);
      else setPageIndex(next.pageIndex);
    },
    // A header click flips between ascending and descending; there's no "unsorted" third step.
    enableSortingRemoval: false,
    // Every column's first click is ascending. Left to itself the table would start any column
    // whose values aren't strings (numbers, the tag list) at descending.
    sortDescFirst: false,
    enableMultiSort: false,
    // The page is reset explicitly above, only for the changes that should reset it.
    autoResetPageIndex: false,
  });

  // A failed background refetch (e.g. on returning to the tab after the data went stale) keeps the
  // rows already on screen: only a view that never loaded shows the error.
  const neverLoaded = query.data === undefined;
  const filteredRows = table.getPrePaginatedRowModel().rows.map((row) => row.original);

  // Nothing about the failure is logged or sent to telemetry: the error may carry patient data.
  const runExport = () => {
    if (exportToast.current !== null) {
      dismiss(exportToast.current);
      exportToast.current = null;
    }
    try {
      const csv = buildCsv(filteredRows, dataset.fields, {
        header: (label) => t(label),
        yes: t('memberViews.format.yes'),
        no: t('memberViews.format.no'),
      });
      downloadFile(csv, dataset.csvFileName({ date: new Date() }), 'text/csv');
      setExportError(null);
    } catch (error) {
      setExportError(error);
      exportToast.current = toast({
        title: t('memberViews.report.error.title'),
        description: t('memberViews.report.error.body'),
        variant: 'destructive',
        // Infinite duration needs an explicit way out, so the dismiss button is not optional here.
        duration: Number.POSITIVE_INFINITY,
        closeButton: true,
        action: { label: t('memberViews.report.error.retry'), onClick: () => latestExport.current() },
      });
    }
  };

  // The toast outlives the render that raised it, so its Try again reaches for the latest export:
  // a letter picked since the failure changes what gets downloaded.
  const latestExport = useRef(runExport);
  useLayoutEffect(() => {
    latestExport.current = runExport;
  });

  // The toast lives in the app-wide toaster, outside the routes, and its Try again holds the rows.
  // Leaving the view — a logout included — dismisses it, so no one can download a previous
  // session's patients from a leftover toast.
  useEffect(
    () => () => {
      if (exportToast.current !== null) dismiss(exportToast.current);
    },
    [dismiss],
  );

  const state: DatasetState =
    query.status === 'pending' || (neverLoaded && query.status === 'error' && query.isFetching)
      ? 'loading'
      : neverLoaded && query.status === 'error'
        ? 'error'
        : rows.length === 0
          ? 'empty'
          : 'loaded';

  return {
    state,
    retry: () => {
      void query.refetch();
    },
    alpha: {
      letters,
      selected: letter,
      onSelect: (next) => {
        setLetter(next);
        setPageIndex(0);
      },
    },
    sort: { state: sorting, onChange: onSortingChange },
    pagination: {
      pageSize,
      page: currentPageIndex + 1,
      pageCount: table.getPageCount(),
      setPageSize,
      setPage: (page) => setPageIndex(page - 1),
    },
    pageRows: table.getRowModel().rows.map((row) => row.original),
    filteredRows,
    search: { value: search, onChange: setSearch },
    table,
    export: {
      run: runExport,
      pending: false,
      error: exportError,
      retry: () => latestExport.current(),
    },
  };
}
